import React from "react"
import {
    FilePdfOutlined,
    EditOutlined,
    CheckCircleTwoTone,
    CloseCircleTwoTone,
    InfoCircleTwoTone,
    FireOutlined,
} from "@ant-design/icons"
import { Badge, Space, Tag, Tooltip, Typography } from "antd"
import classnames from "classnames"
import moment, { MomentFormatSpecification } from "moment-timezone"
import { blue, green, grey, orange, red, yellow } from "@ant-design/colors"
import { Link } from "react-router-dom"
import Button from "../Button/Button"
import css from "./TableColumnRenderers.module.css"
import {
    PaymentInterval,
    BookerPaymentMethodInput,
    PaymentType,
    Gender,
    Instructor,
    CourseLesson,
    CourseType,
    Location,
    SalutationType,
    Venue,
    GeneralLocationContact,
    Course,
    Maybe,
    MonthlySchedule,
    GetCourseDetailsByIdPublicQuery,
    DiscountUnit,
    BookerAttendeeInput,
} from "../../generated/graphql"
import { CurrencyShort, renderAmount } from "../../helpers/renderAmount"
import { renderPercent as renderPercentHelper } from "../../helpers/renderPercent"
import { paymentMethodTypeLabels } from "../PaymentMethodBadge/PaymentMethodBadge"
import { getPaymentMethodType } from "../../helpers/getPaymentMethodType"
import { colorLightGrey } from "../../styles/colors"
import { renderAsMoney } from "../../helpers/renderAsMoney"
import { isDefined, ISODateString } from "../../helpers/typeScriptHelpers"
import { naturalLanguageAge } from "../../helpers/natualLanguageAge"
import { BookingType } from "../../types"
import "moment/locale/de"
import { Color, getTextColor } from "../../helpers/getTextColor"
import { getWindowDimensions, MediaSize } from "../../layouts/Main/GetWindowDimensions/GetWindowDimensions"
import { getTotalCoursePrice } from "../../helpers/getTotalCoursePrice"
import { CourseTableType } from "../../containers/CoursesTable/CourseTableColumns"
import { CurrencySettings } from "../../helpers/currencyHelper"
import { SadIcon, ShockIcon, SmileyIcon } from "../../assets/icons/smily/Smily"
import { MemberIconSvg, PrerequisiteCoursesIconSvg } from "../../assets/icons/OtherIcons"
import { RestrictionsType, getBookingRestrictions } from "../../containers/common/helpers"
import i18n from "../../i18n"

type ReqiredPaymentData = Pick<
    BookerPaymentMethodInput,
    | "id"
    | "cash"
    | "creditCardAccountholder"
    | "creditcardNumber"
    | "expirationDate"
    | "validFrom"
    | "cvc"
    | "paypalAccountholder"
    | "email"
    | "iban"
    | "bank"
    | "bic"
    | "sepaAccountholder"
>

export const renderTitle = (title: string) => {
    return <div className={classnames(css.header)}>{title}</div>
}

export const renderTextStandard = (text: string | undefined | number | null, bold?: boolean) => {
    const classes = bold ? classnames(css.text__standard, css.text__bold) : classnames(css.text__standard)

    return <div className={classes}>{text ?? ""}</div>
}

export const renderUnknown = (text: unknown) => {
    if (typeof text === "string" || typeof text === "number") {
        return renderTextStandard(text)
    }

    return <div>{"!!Attention!!"}</div>
}

type TextColoredInterface = {
    text: string | number
    color: string
    centered?: boolean
}

export const renderTextColored = ({ text, color, centered }: TextColoredInterface) => {
    return (
        <div
            style={{ backgroundColor: color || "transparent" }}
            // className={classnames(centered ? (css.text__colored css.text__centered) : (css.text__colored))}
            className={centered ? classnames(css.text__colored, css.text__centered) : css.text__colored}
        >
            <span>{text}</span>
        </div>
    )
}

export const renderBill = (text: string, cb: () => void) => {
    return (
        <div tabIndex={0} role='button' onKeyDown={cb} onClick={cb} className={classnames(css.bill)}>
            <FilePdfOutlined style={{ paddingRight: "8px" }} />
            <span>{text}</span>
        </div>
    )
}

export const renderFirstCourseLesson = (startDateTime: string | Date, endDateTime: string | Date) => {
    return (
        <>
            <Space size={0} direction='vertical'>
                <div>
                    {/* <div>{startDateTime && moment(startDateTime).format("dd. DD MMM yy")}</div> */}
                    <div style={{ whiteSpace: "nowrap", fontWeight: 550, lineHeight: 1.3 }}>
                        {startDateTime && moment(startDateTime).format("dd DD. MMM YY")}
                    </div>
                </div>
                <div /* className={css.time} */>
                    <div
                        style={{
                            color: colorLightGrey,
                            whiteSpace: "nowrap",
                            lineHeight: 1.3,
                        }}
                    >
                        {moment(startDateTime).format("HH:mm") + " - " + moment(endDateTime).format("HH:mm")}
                    </div>
                </div>
            </Space>
        </>
    )
}

// export const renderFirstCourseLesson = (startDateTime: string | Date, endDateTime: string | Date) => {
//     return (
//         <>
//             <Space size={1} /* className={css.firstCourseLesson} */ direction='vertical'>
//                 <div /* className={css.date} */>
//                     <div /* className={css.firstCourseLessonDate} */>
//                         {startDateTime && moment(startDateTime).format("dd. DD MMM yy")}
//                     </div>
//                     {/* <div className={css.firstCourseLessonDateShort}>
//                         {startDateTime && moment(startDateTime).format("dd. DD MMM yy")}
//                     </div> */}
//                 </div>
//                 <div /* className={css.time} */>
//                     <div
//                         style={{
//                             color: colorLightGrey,
//                             // lineBreak: "strict",
//                         }}
//                         // className={css.firstCourseLessonTime}
//                     >
//                         {moment(startDateTime).format("HH:mm") + " - " + moment(endDateTime).format("HH:mm")}
//                     </div>
//                 </div>
//             </Space>
//         </>
//     )
// }

export const renderCoursePrice = (
    course: CourseTableType | GetCourseDetailsByIdPublicQuery["courseByIdPublic"],
    memberDiscountText: string,
    mediaSize: MediaSize,
    currency?: CurrencySettings,
    courseDetails?: boolean
) => {
    const { coursePrice, paymentInterval, monthlySchedule, lessonCount, paymentType, installmentRates } = course
    const { discount, discountUnit, grossPrice } = coursePrice[0]
    const smartPhoneLayout = mediaSize.media === "xs" || courseDetails

    const memberPartsStyle: React.CSSProperties = {
        whiteSpace: "nowrap",
    }

    const memberPriceInEuro = (originalPrice: number, monthlyConsistent?: boolean) => {
        const installmentRateCount = installmentRates?.length ?? 1
        const totalPrice = monthlyConsistent ? originalPrice * installmentRateCount : originalPrice
        const iconSize = 13
        const memberStyle: React.CSSProperties = {
            width: "fit-content",
            display: "flex",
            alignContent: "center",
            justifyContent: "center",
            backgroundColor: blue[1],
            opacity: 0.8,
            padding: smartPhoneLayout ? "0px 7px 0px 5px" : "3px 7px 0px 5px",
            marginRight: -7,
            height: iconSize + 6,
            borderRadius: iconSize - 4,
            alignSelf: "flex-end",
            color: blue[8],
            marginLeft: smartPhoneLayout ? 10 : "unset",
        }

        if (discount && discountUnit) {
            if (discountUnit === DiscountUnit.Euro) {
                const memberPrice = monthlyConsistent
                    ? (totalPrice - discount) / installmentRateCount
                    : originalPrice - discount

                return (
                    <Space size={3} style={memberStyle}>
                        <FireOutlined />
                        {smartPhoneLayout ? <span style={memberPartsStyle}>{`${memberDiscountText}: `}</span> : null}
                        {monthlyConsistent && <span style={{ ...memberPartsStyle, opacity: 0.7 }}>mtl.</span>}
                        <span style={memberPartsStyle}>
                            {renderAmount({
                                amount: memberPrice,
                                short: false,
                                currency: (currency?.short as CurrencyShort) ?? undefined,
                            })}
                        </span>
                    </Space>
                )
            }

            const discountInPercentage = discount
            const discountPerInstallmentInEuro = originalPrice - (originalPrice * discountInPercentage) / 100
            const memberPrice = discountPerInstallmentInEuro

            return (
                <Space size={3} style={{ ...memberStyle }}>
                    <FireOutlined />
                    {smartPhoneLayout ? <span style={memberPartsStyle}>{`${memberDiscountText}: `}</span> : null}
                    <span style={memberPartsStyle}>
                        {renderAmount({
                            amount: memberPrice,
                            short: false,
                            currency: (currency?.short as CurrencyShort) ?? undefined,
                        })}
                    </span>
                </Space>
            )
        }

        return null
    }

    const totalCoursePrice = getTotalCoursePrice({
        coursePrice: grossPrice,
        lessonCount: lessonCount ?? undefined,
        paymentInterval: paymentInterval ?? undefined,
    })

    // Späteinstieg-Preis (Late-Entry): Liegt eine Preis-Quote mit echter Reduktion vor,
    // wird der reduzierte Buchungspreis (serviceGrossPrice) prominent und der ungekürzte
    // Kurspreis (originalGrossPrice) durchgestrichen daneben gezeigt — plus eine Hinweiszeile
    // "Preis für die verbleibenden X von Y Kurseinheiten". Rein additiv: ohne Quote unverändert.
    // Alle Werte kommen vom Server (Euro-Einheit wie grossPrice); keine Client-Berechnung.
    const bookingPriceQuote = course.bookingPriceQuote
    const showLateEntryPrice = Boolean(bookingPriceQuote && bookingPriceQuote.reduction > 0)

    const renderLateEntryPrice = () => {
        if (!bookingPriceQuote) {
            return null
        }

        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: smartPhoneLayout ? "flex-start" : "flex-end",
                }}
            >
                <Space size={6}>
                    <span style={memberPartsStyle}>
                        {renderAmount({
                            amount: bookingPriceQuote.serviceGrossPrice,
                            currency: currency?.short as CurrencyShort,
                        })}
                    </span>
                    <span style={{ ...memberPartsStyle, textDecoration: "line-through", opacity: 0.55, fontWeight: 400 }}>
                        {renderAmount({
                            amount: bookingPriceQuote.originalGrossPrice,
                            currency: currency?.short as CurrencyShort,
                        })}
                    </span>
                </Space>
                <span
                    style={{
                        fontWeight: 400,
                        fontSize: 11,
                        opacity: 0.75,
                        lineHeight: 1.25,
                        whiteSpace: "normal",
                        textAlign: smartPhoneLayout ? "left" : "right",
                    }}
                >
                    {i18n.t("lateEntry.priceForXofY", {
                        defaultValue: "Preis für die verbleibenden {{eligible}} von {{total}} Kurseinheiten",
                        eligible: bookingPriceQuote.eligibleLessonCount,
                        total: bookingPriceQuote.totalLessonCount,
                    }) as string}
                </span>
            </div>
        )
    }

    const renderMonthlyConsistentPrice = (installmentRates: CourseTableType["installmentRates"]) => {
        if (installmentRates?.[0]) {
            const amount = installmentRates[0].installmentAmount
            return (
                <div
                    style={{
                        display: "flex",
                        // flexDirection: "column",
                        flexDirection: smartPhoneLayout ? "row" : "column",
                        justifyContent: "flex-end",
                    }}
                >
                    <div>
                        <Tooltip title={`Kursdauer ${installmentRates.length} Monate`}>
                            <InfoCircleTwoTone twoToneColor={blue[5]} style={{ marginRight: 5, marginBottom: 1 }} />
                        </Tooltip>
                        <span style={{ opacity: 0.7 }}>{"mtl. "}</span>
                        <span style={memberPartsStyle}>
                            {renderAmount({
                                amount,
                                short: false,
                                currency: (currency?.short as CurrencyShort) ?? undefined,
                            })}
                        </span>
                    </div>
                    {memberPriceInEuro(amount, true)}
                </div>
            )
        }

        return "-"
    }

    const renderContent = () => {
        if (paymentType === PaymentType.Monthly && monthlySchedule === MonthlySchedule.Consistent && installmentRates) {
            return (
                <div
                    className={classnames([css.text__standard, css.text__bold, css.col_coursePrice])}
                    style={{
                        display: "flex",
                        flexDirection: smartPhoneLayout ? "row-reverse" : "column",
                        alignItems: "flex-end",
                        justifyContent: "flex-end",
                    }}
                >
                    {renderMonthlyConsistentPrice(installmentRates)}
                </div>
            )
        }

        return (
            <div
                className={classnames([css.text__standard, css.text__bold, css.col_coursePrice])}
                style={{
                    display: "flex",
                    flexDirection: smartPhoneLayout ? "row" : "column",
                    alignItems: smartPhoneLayout ? "flex-start" : "flex-end",
                }}
            >
                {showLateEntryPrice ? (
                    // Bei aktivem Späteinstieg-Preis ersetzt die Gegenüberstellung den einfachen
                    // Preis; die (client-gerechnete) Mitgliederpreis-Zeile entfällt bewusst, um
                    // keinen Betrag zu zeigen, der dem Server-Buchungspreis widerspricht.
                    renderLateEntryPrice()
                ) : (
                    <>
                        <div style={memberPartsStyle}>
                            {renderAmount({ amount: totalCoursePrice, currency: currency?.short as CurrencyShort })}
                        </div>
                        <div>{memberPriceInEuro(totalCoursePrice, false)}</div>
                    </>
                )}
            </div>
        )
    }

    return renderContent()
}

export const renderPercent = (amount: number) => {
    return renderPercentHelper(amount)
}

export const renderDueDate = (date: string, color: string) => {
    return (
        <div className={classnames(css.date__due)}>
            <Badge color={color || "#d9d9d9"} />
            <span>{date && moment(date).format("DD.MM.YYYY")}</span>
        </div>
    )
}

export const renderDueDateColored = (date: Date, customText?: string) => {
    const today = moment()
    const dueDate = moment(date)
    const diffInDays = dueDate.diff(today, "days")

    const color = diffInDays > 0 ? (diffInDays > 7 ? "" : orange[2]) : red[2]

    return (
        <>
            {renderTextColored({
                text: customText ?? moment(date).format("DD.MM.YYYY"),
                color,
                centered: true,
            })}
        </>
    )
}

export const renderPaymentDate = (date?: string | null) =>
    date === null || !date ? (
        <span>{"offen"}</span>
    ) : (
        <Button type='link' size='small'>
            <EditOutlined />
            <span>{date && moment(date).format("DD.MM.YYYY")}</span>
        </Button>
    )

export const renderPaymentType = (type: PaymentType | null | undefined) => {
    let paymentType = "-"

    switch (type) {
        case PaymentType.PrePaid:
            paymentType = "i18n.containers.payments.DonePaymentsList.prePaid()"
            break
        case PaymentType.Bill:
            paymentType = "i18n.containers.payments.DonePaymentsList.bill()"
            break
        case PaymentType.Monthly:
            paymentType = "i18n.containers.payments.DonePaymentsList.monthly()"
            break
        case PaymentType.OnTheSpot:
            paymentType = "i18n.containers.payments.DonePaymentsList.onTheSpot()"
            break
        default:
            paymentType = "-"
    }

    return renderTextStandard(paymentType)
}

export const renderCustomerNumber = (customerNumberPrefix: string, customerNumber: number, className?: string) => (
    <div className={className}>{`${customerNumberPrefix} ${customerNumber}`}</div>
)

export const renderFullName = (firstname?: string, lastname?: string, className?: string) => {
    if (!firstname && !lastname) {
        return <span className={css.secondary}>{" - "}</span>
    }

    return <div className={className}>{`${firstname} ${lastname}`}</div>
}

export const renderAttendeesOfBooker = (
    attendees: Array<{ firstname: string; lastname: string; id: string }> | null
) => {
    if (attendees !== null && attendees.length > 0) {
        return attendees.map((attendee) => {
            return <div key={attendee.id}>{`${attendee.firstname} ${attendee.lastname}`}</div>
        })
    }

    return <div className={css.secondary}>{"i18n.containers.customers.BookersOverview.noAttendees()"}</div>
}

export const renderAddress = (street: string, houseNumber: string, postCode: string, city: string) => {
    return (
        <div>
            <div>
                {street} {houseNumber}
            </div>
            <div>
                {postCode} {city}
            </div>
        </div>
    )
}

export const renderPaymentMethods = (availablePaymentMethods: Array<ReqiredPaymentData> | null) => {
    if (availablePaymentMethods !== null) {
        return availablePaymentMethods.map((method) => {
            return <div key={method.id}>{paymentMethodTypeLabels[getPaymentMethodType(method)]}</div>
        })
    }

    return "-"
}

export const renderPaymentMethod = (paymentMethod: ReqiredPaymentData | null) => {
    if (paymentMethod !== null) {
        return paymentMethodTypeLabels[getPaymentMethodType(paymentMethod)]
    }

    return "-"
}

export const renderBooleanAsCheckmark = (value: boolean, style?: React.CSSProperties) =>
    value ? (
        <CheckCircleTwoTone twoToneColor={green[3]} style={{ fontSize: "1.7em", ...style }} />
    ) : (
        <CloseCircleTwoTone twoToneColor={grey[1]} style={{ fontSize: "1.7em", ...style }} />
    )

export const renderPricePer = (feeAmount?: Maybe<number>, paymentInterval?: Maybe<PaymentInterval>) => {
    const amount = isDefined(feeAmount) ? feeAmount : 0

    switch (paymentInterval) {
        case PaymentInterval.PerCourse:
            return renderAsMoney(amount, "i18n.containers.courses.CourseEdit.perCourse()")
        case PaymentInterval.PerLesson:
            return renderAsMoney(amount, "i18n.containers.courses.CourseEdit.perLesson()")
        default:
            return amount
    }
}

export const renderCourseTypeFee = (feeAmount?: Maybe<number>, paymentInterval?: Maybe<PaymentInterval>) => {
    const amount = isDefined(feeAmount) ? feeAmount : 0

    switch (paymentInterval) {
        case PaymentInterval.PerCourse:
            return renderAsMoney(amount, "i18n.containers.courses.CourseEdit.perCourse()")
        case PaymentInterval.PerLesson:
            return renderAsMoney(amount, "i18n.containers.courses.CourseEdit.perLesson()")
        default:
            return amount
    }
}

export const renderBirthdayAsAge = (value: ISODateString) => {
    return <div className={classnames(css.text__standard)}>{naturalLanguageAge(moment().diff(value, "months"))}</div>
}

export const renderDate = (value: ISODateString, time?: boolean) => {
    return (
        <div className={classnames(css.text__standard)}>
            {moment(value).format("DD.MM.YYYY")}
            <span className={css.secondary}>{time ? ` ${moment(value).format("HH:mm")} Uhr` : null}</span>
        </div>
    )
}

export const renderBookerAndCustomerNumber = (booker?: {
    firstname?: string
    lastname?: string
    customerNumberPrefix?: string
    customerNumber?: number
}) => {
    if (booker) {
        return (
            <div>
                <div>
                    {booker.firstname} {booker.lastname}
                </div>
                <div className={css.secondary}>
                    {booker.customerNumberPrefix}
                    {" - "}
                    {booker.customerNumber}
                </div>
            </div>
        )
    }

    return <span>{"-"}</span>
}

export const renderGender = (gender: Gender) => {
    return <span>{"i18n.containers.common.Gender[gender]()"}</span>
}

export const renderCourseType = ({
    name,
    color,
    style,
}: {
    name: string
    color?: string
    style?: React.CSSProperties
}) => {
    const theColor: Color = { name: color, value: color ? color : blue[3] }
    const windowWidth = getWindowDimensions().width ?? 576
    const mediaSize = windowWidth < 576 ? "xs" : windowWidth < 768 ? "sm" : windowWidth < 992 ? "md" : "xl"
    const characterCount = name.length
    const maxNameLength = mediaSize === "md" ? 24 : 64
    const formatedName = name.length > maxNameLength ? name.substring(0, maxNameLength) + "..." : name
    const actualStyle: React.CSSProperties = {
        backgroundColor: color ? color : blue[3],
        minWidth: "204px",
        borderRadius: "50px",
        "--bg-color": color ? color : blue[3],
        "--text-color": getTextColor(theColor),
        ...style,
        whiteSpace: characterCount > maxNameLength ? "pre-wrap" : undefined,
    } as React.CSSProperties

    return (
        <Tooltip title={name.length > maxNameLength ? name : undefined} trigger={["click"]}>
            <div style={actualStyle} className={css.coloredBadge}>
                <div>{formatedName}</div>
            </div>
        </Tooltip>
    )
}

export const renderCourseNumber = (
    { courseNumber, courseId, editLink }: { courseNumber: string; courseId: string; editLink: string },
    className?: string
) => {
    return (
        <Button
            type='link'
            size='small'
            style={{ fontSize: "1em", fontWeight: 600 }}
            href={editLink.replace(":id", courseId)}
        >
            <span>{courseNumber}</span>
        </Button>
    )
}

// Verfügbarkeits-Smiley nach prozentualem Anteil freier Plätze:
// grün ab 30% frei, darunter (aber > 0) orange, bei 0 frei rot.
// Ohne bekannte Kapazität (maxAttendees 0/null/unbegrenzt) gilt: verfügbar -> grün.
// Dadurch zeigen z.B. 1-Platz-Privatkurse 1 frei -> grün, 0 -> rot, nie orange.
export const AVAILABILITY_GREEN_RATIO = 0.3

type AvailabilityLevel = "free" | "low" | "none"

export const getAvailabilityLevel = (freePlaces: number, maxAttendees?: number | null): AvailabilityLevel => {
    if (freePlaces <= 0) {
        return "none"
    }

    if (!maxAttendees || maxAttendees <= 0) {
        return "free"
    }

    return freePlaces / maxAttendees >= AVAILABILITY_GREEN_RATIO ? "free" : "low"
}

export const renderFreePlaces = (freePlaces: number, maxAttendees?: number | null) => {
    const level = getAvailabilityLevel(freePlaces, maxAttendees)
    const color = level === "free" ? green[2] : level === "low" ? orange[2] : red[2]

    const freePlacesCount = freePlaces < 0 ? 0 : freePlaces

    return <>{renderTextColored({ text: freePlacesCount, color, centered: true })}</>
}

export const renderStatus = (freePlaces: number, maxAttendees?: number | null, centered?: boolean) => {
    const level = getAvailabilityLevel(freePlaces, maxAttendees)
    const color = level === "free" ? green[5] : level === "low" ? orange[5] : red[3]

    const ShownIcon = ({ color, size }: { color: string; size: number }) => {
        if (level === "free") {
            return <SmileyIcon color={color} size={size} />
        } else if (level === "low") {
            return <ShockIcon color={color} size={size} />
        } else {
            return <SadIcon color={color} size={size} />
        }
    }

    return (
        <div style={{ width: "100%", display: "flex", justifyContent: centered ? "center" : "flex-start" }}>
            <ShownIcon size={24} color={color} />
        </div>
    )
}

export const renderInstructors = (instructors: Array<Pick<Instructor, "id" | "firstname" | "lastname">>) => {
    return (
        <div>
            {instructors.map((instructor, index) => {
                return (
                    <div key={instructor.id}>
                        {instructor.firstname + " " + instructor.lastname}
                        {index + 1 < instructors.length ? "," : null}
                    </div>
                )
            })}
        </div>
    )
}

export const renderCourseLessons = (
    lessons: Array<Pick<CourseLesson, "id" | "startDateTime" | "endDateTime">>,
    showTime?: boolean
) => {
    return lessons.map((lesson, index) => {
        return (
            <div key={lesson.id}>
                <span className={css.primary}>{index + 1 + " - "}</span>
                <span>{moment(lesson.startDateTime).format("DD.MM.YY")}</span>
                {showTime ? (
                    <span className={css.secondary}>
                        {" | " +
                            moment(lesson.startDateTime).format("HH:mm") +
                            "-" +
                            moment(lesson.endDateTime).format("HH:mm")}
                    </span>
                ) : null}
                {/* {index + 1 < lessons.length ? "," : null} */}
            </div>
        )
    })
}

export const renderMonthAsAge = (months?: Maybe<number>) => {
    if (isDefined(months)) {
        if (months > 0) {
            return naturalLanguageAge(months)
        } else {
            return "0"
        }
    }

    return "∞"
}

/**
 * Formatiert die Altersspanne eines Kurses als lesbaren Text.
 *
 * Eine fehlende Grenze (`null`/`undefined` oder <= 0) bedeutet "keine Grenze".
 * - Keine Grenzen gesetzt  -> "Alle Altersgruppen" (statt des verwirrenden "∞ - ∞").
 * - Nur Untergrenze gesetzt -> "ab {Alter}".
 * - Nur Obergrenze gesetzt  -> "bis {Alter}".
 * - Beide Grenzen gesetzt   -> "{min} - {max}" (unveraendertes Verhalten).
 */
export const renderAgeRange = (minAge?: Maybe<number>, maxAge?: Maybe<number>): string => {
    const hasMin = isDefined(minAge) && minAge > 0
    const hasMax = isDefined(maxAge) && maxAge > 0

    if (!hasMin && !hasMax) {
        return "Alle Altersgruppen"
    }
    if (hasMin && !hasMax) {
        return `ab ${renderMonthAsAge(minAge)}`
    }
    if (!hasMin && hasMax) {
        return `bis ${renderMonthAsAge(maxAge)}`
    }

    return `${renderMonthAsAge(minAge)} - ${renderMonthAsAge(maxAge)}`
}

export const renderContinuousText = (text: Maybe<string>) => {
    return (
        <>
            <Tooltip title={text}>
                <Typography.Text ellipsis={true} style={{ maxWidth: 200 }} /* style={COLUMN_STYLE} */>
                    {text}
                </Typography.Text>
            </Tooltip>
        </>
    )
}

export const renderLinkedCourseTypes = (courseTypes: Array<Pick<CourseType, "id" | "name" | "color">>) => {
    return courseTypes.map((courseType, index) => {
        // return <Tag key={courseType.id}>{courseType.name}</Tag>;
        return (
            <div key={courseType.id} style={{ margin: 4 }}>
                {renderCourseType({
                    name: courseType.name,
                    color: courseType.color,
                })}
            </div>
        )
    })
}

export const renderJobTitle = (level?: number) => {
    if (level === 0) {
        return <span>{"i18n.containers.staff.StaffCreate.steps.workData.jobTitle.instructorLevel[level]()"}</span>
    } else if (level === 1) {
        return <span>{"i18n.containers.staff.StaffCreate.steps.workData.jobTitle.instructorLevel[level]()"}</span>
    }

    return "-"
}

export const renderAssignedLocations = (locations: Array<Pick<Location, "id" | "name">>) => {
    return locations.map((location) => {
        return <div key={location.id}>{`${location.name}`}</div>
    })
}

export const renderSalutation = (salutation: SalutationType) => {
    return <span>{"i18n.containers.staff.StaffCreate.steps.personalData.salutation.options[salutation]()"}</span>
}

export const renderVenues = (venues: Array<Pick<Venue, "id" | "name">>) => {
    return venues.map((venue, index) => {
        return (
            <div key={venue.id}>
                {venue.name}
                {index + 1 < venues.length ? "," : null}
            </div>
        )
    })
}

export const renderGeneralLocationContact = (contact: Pick<GeneralLocationContact, "email" | "phoneNumber">) => {
    return (
        <>
            <div>{contact.email}</div>
            <div>{contact.phoneNumber}</div>
        </>
    )
}

export const renderCourses = (courses: Maybe<Array<Pick<Course, "id" | "prefixedCourseNumber">>>) => {
    if (courses) {
        return courses.map((course, index) => {
            return (
                <div key={course.id}>
                    {course.prefixedCourseNumber}
                    {index + 1 < courses.length ? "," : null}
                </div>
            )
        })
    }

    return "-"
}

export const renderBookingType = (bookingType: Extract<BookingType, string> | string) => {
    switch (bookingType) {
        case "Booking":
            return renderTextColored({
                text: "i18n.containers.bookings.BookingList.bookingTypes[bookingType]()",
                color: blue[1],
                centered: true,
            })
        case "Waitlist":
            return renderTextColored({
                text: "i18n.containers.bookings.BookingList.bookingTypes[bookingType]()",
                color: yellow[1],
                centered: true,
            })
        case "PlaceReservation":
            return renderTextColored({
                text: "i18n.containers.bookings.BookingList.bookingTypes[bookingType]()",
                color: red[1],
                centered: true,
            })

        default:
            return <span>{bookingType}</span>
    }
}

export const renderBookingNumber = (
    bookingNumberPrefix: string,
    bookingNumber: number,
    bookingType?: Extract<BookingType, string> | string,
    className?: string
) => {
    const color = () => {
        switch (bookingType) {
            case "Booking":
                return blue[5]
            case "Waitlist":
                return yellow[5]
            case "PlaceReservation":
                return red[5]

            default:
                return blue[5]
        }
    }

    return (
        <Badge
            color={color()}
            text={
                <span
                    className={classnames(className, css.bookingNumber)}
                >{`${bookingNumberPrefix}-${bookingNumber}`}</span>
            }
        />
    )
}

export const renderDetailsButton = (detailsLink: string, id: string, record: unknown, tableData: unknown) => {
    return (
        <Button type='link' size='small'>
            <Link
                to={{
                    pathname: detailsLink.replace(":id", id),

                    state: { record, tableData },
                }}
            >
                {"i18n.containers.invoice.Invoice.details()"}
            </Link>
        </Button>
    )
}

export const renderSettled = (settledBookingCount: number, bookingCount: number) => {
    return <div>{`${settledBookingCount} ${"i18n.containers.invoice.Invoice.of()"} ${bookingCount}`}</div>
}

export const renderDateFormatted = (value: ISODateString, format?: MomentFormatSpecification) => {
    if (typeof format === "string") {
        return <div className={classnames(css.text__standard)}>{moment(value).format(format)}</div>
    }

    return <div className={classnames(css.text__standard)}>{moment(value).format("DD.MM.YYYY")}</div>
}

export const renderDateAsWeekdayTag = ({
    value,
    style,
    color,
}: {
    value: ISODateString
    style?: React.CSSProperties
    color?: string
}) => {
    const actualColor = color ?? blue[3]
    const weekday = moment(value).format("dddd")

    return (
        <Tag style={style} color={actualColor}>
            {weekday}
        </Tag>
    )
}

export const renderStartEndTime = ({
    startTime,
    endTime,
    format,
}: {
    startTime: ISODateString
    endTime: ISODateString
    format?: MomentFormatSpecification
}) => {
    const actualFormat = typeof format === "string" ? format : "HH:mm"

    return <div>{`${moment(startTime).format(actualFormat)} - ${moment(endTime).format(actualFormat)} Uhr`}</div>
}

export const renderDuration = ({
    startTime,
    endTime,
    format,
}: {
    startTime: ISODateString
    endTime: ISODateString
    format?: MomentFormatSpecification
}) => {
    // Runden statt abschneiden: gespeicherte Lesson-Zeiten können Sekunden-/
    // Millisekundenanteile enthalten (z.B. 10:00:23 - 11:00:00 = 59,6min → 60min)
    const durationInMinutes = Math.round(moment(endTime).diff(moment(startTime), "minutes", true))

    return <div>{`${durationInMinutes}min`}</div>
}

export const renderEntranceFee = (entranceFee: number | null | undefined, mediaSize: MediaSize, { symbol = "€", decimalSeperator = "," }: Partial<CurrencySettings> = {}) => {
    const { media } = mediaSize
    const containerStyle: React.CSSProperties = {
        display: "flex",
        justifyContent: "left",
        alignItems: "center",
        whiteSpace: "nowrap",
        cursor: "pointer",
    }

    if (entranceFee === undefined) {
        return "-"
    } else if (entranceFee === null) {
        return (
            <Tooltip
                trigger={["click"]}
                placement={media !== "sm" ? "topRight" : "top"}
                autoAdjustOverflow={false}
                title='Der Eintrittspreis für das Bad ist im Kurspreis bereits enthalten.'
            >
                <div style={containerStyle}>
                    <Badge color='green' style={{ marginRight: 3 }} />
                    inkl.
                    <InfoCircleTwoTone twoToneColor={blue[5]} style={{ marginLeft: 5 }} />
                </div>
            </Tooltip>
        )
    } else if (entranceFee === 0) {
        return (
            <Tooltip
                trigger={["click"]}
                placement={media !== "sm" ? "topRight" : "top"}
                autoAdjustOverflow={false}
                title={`Der Eintrittspreis für das Bad ist im Kurspreis nicht enthalten.`}
            >
                <div style={containerStyle}>
                    <Badge color='red' style={{ marginRight: 3 }} />
                    <span style={{ whiteSpace: "nowrap" }}>nicht inkl.</span>

                    <InfoCircleTwoTone twoToneColor={blue[5]} style={{ marginLeft: 5 }} />
                </div>
            </Tooltip>
        )
    }

    const value = entranceFee.toFixed(2).toString().replace(".", decimalSeperator)

    return (
        <Tooltip
            trigger={["click"]}
            placement={media !== "sm" ? "topRight" : "top"}
            autoAdjustOverflow={false}
            title={`Der Eintrittspreis für das Bad von ${value} ${symbol} pro Kurseinheit ist nicht im Kurspreis enthalten.`}
        >
            <div style={containerStyle}>
                <Badge color='red' style={{ marginRight: 3 }} />
                {media !== "sm" && <span>zzgl.</span>}

                <span style={{ whiteSpace: "nowrap", marginLeft: 3 }}>{` ${value} ${symbol}`}</span>

                <InfoCircleTwoTone twoToneColor={blue[5]} style={{ marginLeft: 5 }} />
            </div>
        </Tooltip>
    )
}

export const renderAdditionalShortInfo = ({ text, maxWidth }: { text?: string; maxWidth?: number | string }) => {
    return <div style={{ whiteSpace: "pre-wrap", maxWidth, lineHeight: 1.3 }}>{text}</div>
}

// Kurzinfo als volle Unterzeile (Konzept 1): Im Desktop-Table wird die Kurzinfo
// nicht in eine schmale Spalte gequetscht (das blähte die Zeilenhöhe auf), sondern
// als kompakte Unterzeile direkt unter dem Kurs gerendert. Abstände/Trenner kommen
// aus CourseTable.module.less (.kurzinfoTable / .kurzinfoMainRow) — hier nur der
// reine Textinhalt (kein eigenes Band), damit es als eine Einheit mit dem Kurs wirkt.
export const renderShortInfoSubRow = (text?: string | null, label: string = "Kurzinfo") => {
    if (!text) {
        return null
    }

    return (
        <div style={{ color: "#6b6b6b", fontSize: 12.5, lineHeight: 1.35, whiteSpace: "pre-wrap" }}>
            <span style={{ fontWeight: 600, color: "#8a8a8a", marginRight: 6 }}>{`${label}:`}</span>
            {text}
        </div>
    )
}

export const renderRestrictions = ({ record, media }: { record: CourseTableType; media: string }) => {
    const restrictions: RestrictionsType = getBookingRestrictions(record?.bookingRestriction)
    const { membersOnly, prerequisiteCourses } = restrictions
    const membersOnlyText = "Nur für Mitglieder"
    const prerequisiteCoursesText = "Voraussetzungskurse"

    if (membersOnly && prerequisiteCourses) {
        return (
            <Space direction='vertical' size={1}>
                <Space>
                    <MemberIconSvg color={green[5]} size={28} />
                    {media === "xs" && membersOnlyText}
                </Space>
                <Space>
                    <PrerequisiteCoursesIconSvg color={orange[5]} size={20} style={{ marginTop: 6 }} />
                    {media === "xs" && prerequisiteCoursesText}
                </Space>
            </Space>
        )
    } else if (membersOnly) {
        return (
            <Space>
                <MemberIconSvg color={green[5]} size={30} />
                {media === "xs" && membersOnlyText}
            </Space>
        )
    } else if (prerequisiteCourses) {
        return (
            <Space>
                <PrerequisiteCoursesIconSvg color={orange[5]} size={22} style={{ marginTop: 6 }} />
                {media === "xs" && prerequisiteCoursesText}
            </Space>
        )
    } else return null
}

export const renderRestrictionsAttendeeTable = ({ record, media }: { record: BookerAttendeeInput; media: string }) => {
    const isMember = record.member

    if (isMember) {
        return (
            <Space>
                <MemberIconSvg color={green[5]} size={26} />
                {/* {media === "xs" && "Nicht Mitglied"} */}
            </Space>
        )
    }

    return null
}
